import { createContext, useContext } from 'react';
import { useMapInstance } from '@/utils/context/mapInstanceContext';
import {
  OlMapSources,
  useOlMapSources,
} from '@/components/Map/MapViewer/utils/config/useOlMapSources';

export const MapSourcesContext = createContext<OlMapSources | null>(null);

/**
 * Returns the store-driven {@link OlMapSources} shared by every map that renders
 * the diagram (main map + magnifier loupe). Must be used within a
 * {@link MapSourcesProvider}.
 */
export const useMapSources = (): OlMapSources => {
  const context = useContext(MapSourcesContext);
  if (!context) {
    throw new Error('useMapSources must be used within a MapSourcesProvider');
  }
  return context;
};

export interface MapSourcesProviderProps {
  children: React.ReactNode;
}

/**
 * Builds the shared map sources once (against the primary map instance) and
 * provides them to every consumer below. Lifting source ownership here lets the
 * loupe render the exact same {@link VectorSource} instances as the main map,
 * avoiding duplicated feature building.
 */
export const MapSourcesProvider = ({ children }: MapSourcesProviderProps): JSX.Element => {
  const { mapInstance } = useMapInstance();
  const sources = useOlMapSources({ mapInstance });

  return <MapSourcesContext.Provider value={sources}>{children}</MapSourcesContext.Provider>;
};
